/**
 * PalmExposureDetector — Phase 3-B (Slide 18, 5꼭지 "다음 3").
 *
 * MediaPipe Hand 21 landmark에서 손바닥 polygon을 추정해 그 면적을
 * 정상 손바닥 노출 면적과 비교한다.
 *
 * 시그너처:
 *   1. 손바닥이 카메라를 정면으로 향함 → polygon 면적이 큼 (정상).
 *   2. 손바닥이 책상을 향해 뒤집힘 → polygon 면적이 작음.
 *      또는 손바닥이 사라짐 (다른 물체에 가려짐).
 *   3. 손바닥 뒤집힘이 sustained → 손바닥 비노출 지속 시그널
 *      (관찰만, 해석 없음).
 *
 * 손바닥 polygon: landmark idx 0 (wrist), 1 (thumb CMC), 5 (index MCP),
 * 9 (middle MCP), 13 (ring MCP), 17 (pinky MCP). 6각형으로 shoelace
 * 공식 면적 계산. 픽셀이 아닌 normalised 좌표 기준이라 0..1 영역.
 *
 * Sustained occlusion 판정: 연속 ≥ 1.5s 동안 polygon area 가 임계 미만
 * (default 0.005, normalised square units — 손바닥은 보통 0.01-0.04).
 */

export interface PHandLm { x: number; y: number; z?: number; visibility?: number }

export interface PalmExposureResult {
  /** 현재 프레임 palm polygon 면적 (normalised 0..1²). 0 = 손 없음. */
  area: number;
  /** 평균 추적 면적 (0.5s 평균) — 일시적 깜빡임 노이즈 제거. */
  smoothArea: number;
  /** 카메라를 정면으로 향한 정상 노출인지 여부 (smoothArea ≥ 임계). */
  exposed: boolean;
  /** 손바닥 비노출 지속: 손바닥이 ≥ sustainSec 동안 잘 안 보임. */
  hiddenSustained: boolean;
  /** 손바닥 안 보이는 상태 지속 시간 (s). exposed=true면 0. */
  hiddenSec: number;
}

interface Sample { t: number; area: number }

export class PalmExposureDetector {
  /** 평균/지속 측정용 sliding window (ms). */
  private readonly windowMs: number;
  /** Area < threshold → 손바닥 안 보임. normalised square units. */
  private readonly exposedThresh: number;
  /** 비노출 지속 발동 sustain 시간 (s). */
  private readonly sustainSec: number;
  private samples: Sample[] = [];
  private hiddenStart: number | null = null;

  constructor(opts: { windowMs?: number; exposedThresh?: number; sustainSec?: number } = {}) {
    this.windowMs = opts.windowMs ?? 500;
    this.exposedThresh = opts.exposedThresh ?? 0.005;
    this.sustainSec = opts.sustainSec ?? 1.5;
  }

  reset(): void {
    this.samples = [];
    this.hiddenStart = null;
  }

  update(landmarks: PHandLm[] | null | undefined, now: number): PalmExposureResult {
    let area = 0;
    if (landmarks && landmarks.length >= 18) {
      const palmIdx = [0, 1, 5, 9, 13, 17];
      const verts = palmIdx.map(i => landmarks[i]).filter(p => p && (p.visibility ?? 1) > 0.3);
      if (verts.length >= 4) {
        area = Math.abs(polygonArea(verts as { x: number; y: number }[]));
      }
    }
    this.samples.push({ t: now, area });
    while (this.samples.length && now - this.samples[0].t > this.windowMs) this.samples.shift();
    const smoothArea = this.samples.length
      ? this.samples.reduce((s, a) => s + a.area, 0) / this.samples.length
      : 0;
    const exposed = smoothArea >= this.exposedThresh;
    if (!exposed) {
      if (this.hiddenStart == null) this.hiddenStart = now;
    } else {
      this.hiddenStart = null;
    }
    const hiddenSec = this.hiddenStart == null ? 0 : (now - this.hiddenStart) / 1000;
    const hiddenSustained = hiddenSec >= this.sustainSec;
    return { area, smoothArea, exposed, hiddenSustained, hiddenSec };
  }
}

/** Signed shoelace polygon area. Caller takes abs(). */
function polygonArea(verts: { x: number; y: number }[]): number {
  let a = 0;
  for (let i = 0; i < verts.length; i++) {
    const v0 = verts[i];
    const v1 = verts[(i + 1) % verts.length];
    a += v0.x * v1.y - v1.x * v0.y;
  }
  return a / 2;
}
